import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import styled from "styled-components";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import { useParams } from "react-router-dom";
import TPC_URL from "../../../utils/TPC_URL";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: var(--mui-palette-background-default);
  box-sizing: border-box;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
  background-color: var(--mui-palette-background-paper);
  flex-shrink: 0;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  padding: 16px;
`;

const TopSection = styled.div`
  flex: 1;
  padding: 16px;
  border-bottom: 1px solid var(--mui-palette-divider);
  box-sizing: border-box;
`;

const BottomSection = styled.div`
  flex: 3;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Title = styled(Typography)`
  color: var(--mui-palette-text-primary);
  margin: 0;
`;

// 초 → 시:분:초
const formatTime = (seconds) => {
  if (!seconds || seconds === 0) return "-";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}시 ${minutes}분 ${secs}초`;
  } else if (minutes > 0) {
    return `${minutes}분 ${secs}초`;
  } else {
    return `${secs}초`;
  }
};

// 초 → 분(정수)
const formatMinutes = (seconds) => Math.round(seconds / 60);

function Changeover() {
  const { group_code, project_code } = useParams();

  const [items, setItems] = useState([]);
  const [changeoversRaw, setChangeoversRaw] = useState([]);
  const [loading, setLoading] = useState(false);

  const [defaultChangeover, setDefaultChangeover] = useState(3000); // 초
  const [inputDefaultMinutes, setInputDefaultMinutes] = useState(50); // 텍스트필드용 분 단위

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const [fromGroup, setFromGroup] = useState("");
  const [toGroup, setToGroup] = useState("");
  const [priorityTimeMinutes, setPriorityTimeMinutes] = useState(50);

  const openSnackbar = useCallback((message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // 초기 localStorage → 기본 교체 시간 로드
  useEffect(() => {
    const stored = localStorage.getItem("changeover_default");
    if (stored) {
      const val = Number(stored);
      if (!Number.isNaN(val) && val > 0) {
        setDefaultChangeover(val);
        setInputDefaultMinutes(formatMinutes(val));
      } else {
        localStorage.setItem("changeover_default", "3000");
        setDefaultChangeover(3000);
        setInputDefaultMinutes(50);
      }
    } else {
      localStorage.setItem("changeover_default", "3000");
      setDefaultChangeover(3000);
      setInputDefaultMinutes(50);
    }
  }, []);

  // 텍스트필드 값 변경 (분 단위만 상태로)
  const handleDefaultChangeoverChange = (e) => {
    const minutes = Number(e.target.value);
    if (Number.isNaN(minutes) || minutes <= 0) {
      setInputDefaultMinutes("");
      return;
    }
    setInputDefaultMinutes(minutes);
  };

  // 일괄 교체 시간 적용 버튼 클릭
  const handleApplyDefaultChangeover = async () => {
    const before_time = defaultChangeover; // 현재값 (초)
    const after_time = (Number(inputDefaultMinutes) || 0) * 60; // 수정된 값(초)

    if (!after_time || after_time <= 0) {
      openSnackbar("유효한 교체 시간을 입력해주세요.", "warning");
      return;
    }

    try {
      const payload = {
        group_code,
        project_code,
        before_time,
        after_time,
      };

      const res = await axios.put(
        `${TPC_URL}/api/changeover/batchUpdate`,
        payload
      ); // [web:495][web:498]

      const msg =
        res.data?.message || "일괄 교체 시간이 성공적으로 적용되었습니다.";
      openSnackbar(msg, "success");

      // 상태 & localStorage 업데이트 → 리렌더링
      setDefaultChangeover(after_time);
      localStorage.setItem("changeover_default", String(after_time));
      // 필요 시 재조회
      await loadChangeovers();
    } catch (error) {
      console.error("batchUpdate error:", error);
      openSnackbar(
        `일괄 적용 실패: ${error.response?.data?.message || error.message}`,
        "error"
      );
    }
  };

  const loadChangeovers = useCallback(async () => {
    try {
      const res = await axios.get(
        `${TPC_URL}/api/changeover/getAll?group_code=${group_code}&project_code=${project_code}`
      );
      setChangeoversRaw(res.data || []);
    } catch (e) {
      console.error("changeover getAll error:", e);
      setChangeoversRaw([]);
    }
  }, [group_code, project_code]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes] = await Promise.all([
        axios.get(
          `${TPC_URL}/api/item/getAllItems?group_code=${group_code}&project_code=${project_code}`
        ),
      ]);

      setItems(itemsRes.data || []);
      await loadChangeovers();
    } catch (error) {
      console.error("Error loading data:", error);
      openSnackbar(
        `데이터 로드 오류: ${error.response?.data?.message || error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, openSnackbar, loadChangeovers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // material_grouping 목록 (모달 선택용)
  const materialGroups = useMemo(() => {
    const set = new Set(items.map((it) => it.material_grouping || "미지정"));
    return Array.from(set).sort();
  }, [items]);

  // 우선순위 카드 데이터: 그룹별 대표 시간 + From/To '사내품번'
  const priorityCards = useMemo(() => {
    if (!changeoversRaw.length || !items.length) return [];

    const itemMap = new Map();
    items.forEach((it) => itemMap.set(it.item_id, it));

    const filtered = changeoversRaw.filter(
      (c) => c.changeover_time !== defaultChangeover
    );

    const groups = new Map();
    filtered.forEach((c) => {
      const fromItem = itemMap.get(c.from_item_id);
      const toItem = itemMap.get(c.to_item_id);

      const fromMG = fromItem?.material_grouping || "미지정";
      const toMG = toItem?.material_grouping || "미지정";

      const key = `${fromMG}:::${toMG}`;

      if (!groups.has(key)) {
        groups.set(key, {
          from_material_grouping: fromMG,
          to_material_grouping: toMG,
          sample_time: c.changeover_time,
        });
      }
    });

    return Array.from(groups.values()).sort((a, b) => {
      const fa = `${a.from_material_grouping} -> ${a.to_material_grouping}`;
      const fb = `${b.from_material_grouping} -> ${b.to_material_grouping}`;
      return fa.localeCompare(fb);
    });
  }, [changeoversRaw, items, defaultChangeover]);

  const handleOpenPriorityModal = () => {
    setFromGroup("");
    setToGroup("");
    setPriorityTimeMinutes(formatMinutes(defaultChangeover));
    setPriorityModalOpen(true);
  };

  const handleClosePriorityModal = () => {
    setPriorityModalOpen(false);
  };

  const handleSavePriority = async () => {
    if (!fromGroup || !toGroup) {
      openSnackbar("From / To 그룹을 모두 선택해주세요.", "warning");
      return;
    }
    if (fromGroup === toGroup) {
      openSnackbar("From과 To 그룹은 서로 달라야 합니다.", "warning");
      return;
    }
    const seconds = priorityTimeMinutes * 60;
    const payload = {
      from_material_grouping: fromGroup,
      to_material_grouping: toGroup,
      changeover_time: seconds,
    };
    try {
      const res = await axios.put(`${TPC_URL}/api/changeover/update`, {
        group_code,
        project_code,
        info: payload,
      });
      const msg = res.data?.message || "우선순위 교체 시간이 등록되었습니다.";
      openSnackbar(msg, "success");
      await loadChangeovers();
      setPriorityModalOpen(false);
    } catch (error) {
      console.error("우선순위 교체 시간 등록 실패:", error);
      const errMsg =
        error.response?.data?.message ||
        error.message ||
        "우선순위 교체 시간 등록 중 오류가 발생했습니다.";
      openSnackbar(errMsg, "error");
    }
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Title
          variant="h4"
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "22px !important",
            color: "#111",
          }}
        >
          품명 교체시간
        </Title>
      </HeaderContainer>

      <ContentContainer>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 상단: 일괄 교체 시간 적용 */}
            <TopSection>
              <Typography variant="h6" fontWeight={600} mb={2}>
                일괄 교체 시간 적용
              </Typography>
              <Typography variant="body1" mb={2}>
                교체시간 : {formatMinutes(defaultChangeover)}분
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label="기본 교체 시간(분)"
                  type="number"
                  size="small"
                  value={inputDefaultMinutes}
                  onChange={handleDefaultChangeoverChange}
                  inputProps={{ min: 1 }}
                  sx={{ width: 150 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyDefaultChangeover}
                  sx={{ height: 40 }}
                >
                  적용
                </Button>
              </Box>
            </TopSection>

            {/* 하단: 우선순위 교체 시간 적용 */}
            <BottomSection>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  우선순위 교체 시간 적용
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenPriorityModal}
                >
                  우선순위 교체 시간 추가
                </Button>
              </Box>

              {/* 사내품번 기준 카드 리스트 */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  overflowX: "auto",
                  overflowY: "hidden",
                  pb: 1,
                }}
              >
                {priorityCards.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    기본 교체 시간과 다른 우선순위 교체 시간이 없습니다.
                  </Typography>
                ) : (
                  priorityCards.map((card) => (
                    <Card
                      key={`${card.from_material_grouping}__${card.to_material_grouping}`}
                      sx={{
                        minWidth: 290,
                        maxWidth: 290,
                        height: 140,
                        flex: "0 0 auto",
                        borderRadius: 2,
                        boxShadow: "0 4px 14px rgba(15,23,42,0.08)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        backgroundColor: "#ffffff",
                        border: "1px solid #e5e7eb",
                        padding: "10px",
                      }}
                    >
                      <CardContent sx={{ p: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography
                            color="text.secondary"
                            sx={{ letterSpacing: 0.4 }}
                            variant="h5"
                            fontWeight={700}
                          >
                            FROM
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            color="text.secondary"
                            sx={{ letterSpacing: 0.4 }}
                          >
                            TO
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1.5,
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="h5"
                            fontWeight={600}
                            sx={{
                              flex: 1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {card.from_material_grouping}
                          </Typography>

                          <Typography
                            variant="h5"
                            color="text.disabled"
                            sx={{ mx: 0.5 }}
                          >
                            →
                          </Typography>

                          <Typography
                            variant="h5"
                            fontWeight={600}
                            sx={{
                              flex: 1,
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {card.to_material_grouping}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mb: 0.3 }}
                            >
                              교체 시간
                            </Typography>
                            <Typography
                              variant="h5"
                              fontWeight={700}
                              color="primary"
                              sx={{ lineHeight: 1.1 }}
                            >
                              {formatMinutes(card.sample_time)}분
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            </BottomSection>
          </>
        )}
      </ContentContainer>

      {/* material_grouping 기준 우선순위 교체 시간 추가 모달 */}
      <Dialog
        open={priorityModalOpen}
        onClose={handleClosePriorityModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>우선순위 교체 시간 등록</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="from-group-label">From 사내품번</InputLabel>
              <Select
                labelId="from-group-label"
                label="From 사내품번"
                value={fromGroup}
                onChange={(e) => setFromGroup(e.target.value)}
              >
                {materialGroups.map((mg) => (
                  <MenuItem key={mg} value={mg}>
                    {mg}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="to-group-label">To 사내품번</InputLabel>
              <Select
                labelId="to-group-label"
                label="To 사내품번"
                value={toGroup}
                onChange={(e) => setToGroup(e.target.value)}
              >
                {materialGroups.map((mg) => (
                  <MenuItem key={mg} value={mg} disabled={mg === fromGroup}>
                    {mg}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="교체 시간(분)"
              type="number"
              size="small"
              value={priorityTimeMinutes}
              onChange={(e) =>
                setPriorityTimeMinutes(Math.max(1, Number(e.target.value) || 1))
              }
              inputProps={{ min: 1 }}
              sx={{ width: 150 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePriorityModal}>취소</Button>
          <Button onClick={handleSavePriority} variant="contained">
            등록
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}

export default Changeover;
